/**
 * @file booking.layout.tsx
 * @path apps/website/src/layouts/booking.layout.tsx
 * @description Pure structural layout for the booking flow.
 */

import React, { useState } from "react";
import { Button, Sheet, SheetContent } from "@sirbastians/ui";
import { useIsMobile } from "@/hooks";

interface BookingLayoutProps {
  children: React.ReactNode;
  sidebar?: React.ReactNode;
  footer?: React.ReactNode;
}

export const BookingLayout = ({
  children,
  sidebar,
  footer,
}: BookingLayoutProps) => {
  const isMobile = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <div className="relative flex flex-col min-h-screen bg-background text-foreground">
      {/* 1. Main Grid Container */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-12">
        {/* Sidebar: Visible on Desktop, Hidden on Mobile */}
        {!isMobile && (
          <aside className="md:col-span-4 border-r border-sidebar-border p-2 bg-accent sticky top-0 h-screen overflow-y-auto">
            {sidebar}
          </aside>
        )}

        {/* Content Area */}
        <main className="relative md:col-span-8 flex flex-col ">
          <div className="flex-1 p-6 pb-20">
            {/* Mobile-only toggle for the Summary/Sidebar */}
            {isMobile && sidebar && (
              <Button
                className="self-end mb-4"
                onClick={() => setIsSidebarOpen(true)}
                variant="accent"
                size="sm"
              >
                View Summary
              </Button>
            )}

            <div className="flex-grow">{children}</div>
          </div>

          {/* 2. Sticky Footer */}
          {footer && (
            <footer className="sticky bottom-0 z-20 w-full p-4 border-t bg-background/90 backdrop-blur-sm">
              {footer}
            </footer>
          )}
        </main>
      </div>

      {/* 3. Mobile Sidebar Drawer */}
      <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <SheetContent
          side="bottom"
          className="h-[90vh] overflow-y-auto bg-secondary rounded-t-3xl"
        >
          {sidebar}
        </SheetContent>
      </Sheet>
    </div>
  );
};
